import {
  EvmAddressSummaryInfo,
  type EvmErc20TransferInfo,
  type EvmErc20TransferListInfo,
  type EvmNftCardInfo,
  type EvmNftTransferInfo,
  type EvmTokenCardInfo,
  type EvmTokenCardListInfo,
  type EvmTokenDetailInfo,
  type EvmTokenHolderInfo,
  type EvmTokenHolderListInfo,
  type EvmTokenInfo,
  type EvmTokenItemHolderInfo,
  type EvmTokenSummaryInfo,
  type EvmTokenTransferInfo,
  type NexonNativeTokenInfo,
} from "~/api/repository";
import { IMAGE_BASE_URL } from "~/constants/image.constant";
import { TOKEN_TYPE_NAME } from "~/constants/token.constants";
import { AddressSummaryModel } from "~/types/model/address.model";
import { ListBaseModel } from "~/types/model/base.model";
import { ITokenType } from "~/types/token-type.type";
import { secondsToDate } from "~/utils/dateFormat";
import { setAbbr, setComma, setDecimal, setDigits } from "~/utils/numberFormat";

// start of token, nft base model

/** @description
 * erc20, 721, 1155 토큰 정보 모델
 */
export class TokenSummaryModel {
  private _logo: string;
  private _name: string;
  private _symbol: string;
  private _address: string;

  constructor(data: EvmTokenSummaryInfo) {
    this._logo = data.IMG;
    this._name = data.NN;
    this._symbol = data.SB;
    this._address = data.ADDR;
  }

  get logo(): string {
    return this._logo || IMAGE_BASE_URL + "/token/default.png";
  }

  get symbol(): string {
    return this._symbol;
  }

  get address(): string {
    return this._address;
  }

  get name(): string {
    return this._name;
  }
}

/** @description
 * 트랜스퍼 테이블 row의 베이스 모델 (tokenInfo 필드 제외)
 */
export class BaseTransferModel {
  protected _transactionHash: string;
  protected _method: string;
  protected _fromAddressInfo: EvmAddressSummaryInfo;
  protected _toAddressInfo: EvmAddressSummaryInfo;
  protected _age: string;
  protected _unixTimeStamp: number;
  protected _amount: string;

  constructor(
    data:
      | EvmErc20TransferInfo
      | EvmTokenTransferInfo
      | EvmNftTransferInfo
      | EvmTokenInfo
  ) {
    this._transactionHash = data.TRXHA;
    this._method = data.MTH;
    this._fromAddressInfo = data.ADDRSFROMINFO;
    this._toAddressInfo = data.ADDRSTOINFO;
    this._age = data.AG;
    this._unixTimeStamp = data.UT;
    if ("QTY" in data) {
      this._amount = data.QTY;
    } else {
      this._amount = data.VAL;
    }
  }

  get transactionHash(): string {
    return this._transactionHash;
  }

  get method(): string {
    return this._method;
  }

  get fromAddressInfo(): AddressSummaryModel {
    return new AddressSummaryModel(this._fromAddressInfo);
  }

  get toAddressInfo(): AddressSummaryModel {
    return new AddressSummaryModel(this._toAddressInfo);
  }

  get age(): string {
    return this._age;
  }

  get unixTimeStamp(): string {
    return secondsToDate(this._unixTimeStamp);
  }

  get amount(): string {
    return setComma(this._amount);
  }
}

export class AccountNftTransferModel extends BaseTransferModel {
  protected _caInfo: EvmAddressSummaryInfo;
  protected _tokenId: string;

  constructor(data: EvmTokenInfo) {
    super(data);
    this._caInfo = data.ADDRSCAINFO;
    this._tokenId = data.TKNID;
  }

  get caInfo(): AddressSummaryModel {
    return new AddressSummaryModel(this._caInfo);
  }

  get tokenId(): string {
    return this._tokenId;
  }
}

/** @description
 * erc20, 721, 1155 공통 트랜스퍼 테이블 row 모델 (BaseTransferModel 상속)
 */
export class TokenTransferModel extends BaseTransferModel {
  protected _tokenInfo: EvmTokenSummaryInfo;
  protected _tokenType?: string;

  constructor(
    data: EvmErc20TransferInfo | EvmTokenTransferInfo | EvmNftTransferInfo
  ) {
    super(data);
    if ("TKNSINFO" in data) {
      this._tokenInfo = data.TKNSINFO;
    } else {
      this._tokenInfo = data.TKNCTR;
    }

    // 토큰 타입 저장
    if ("TP" in data) {
      this._tokenType = data.TP;
    }
  }

  get tokenInfo(): TokenSummaryModel {
    return new TokenSummaryModel(this._tokenInfo);
  }

  get tokenType() {
    return this._tokenType as ITokenType | undefined;
  }
}

/** @description
 * erc20, 721, 1155 및 하위 아이템 홀더 테이블 row의 베이스 모델
 */
export class TokenHolderBaseModel {
  protected _holder: EvmAddressSummaryInfo;
  protected _amount: string;
  protected _percentage: string;

  constructor(data: EvmTokenHolderInfo | EvmTokenItemHolderInfo) {
    this._holder = data.HDR;
    this._amount = data.QTY;
    this._percentage = data.PER;
  }

  get holder(): AddressSummaryModel {
    return new AddressSummaryModel(this._holder);
  }

  get amount(): string {
    return setComma(this._amount);
  }

  get percentage(): string {
    return this._percentage;
  }

  get formattedPercentage(): string {
    return setComma(this._percentage) + "%";
  }
}

// end of token, nft base model

/** @description
 * erc20 트랜스퍼 테이블 row 모델
 */

/** @description
 * erc20, erc721, erc1155 홀더 테이블 row 모델
 */
export class TokenHolderModel extends TokenHolderBaseModel {
  private _uniqueTokenCount: string;
  private _volatility: string;

  constructor(data: EvmTokenHolderInfo) {
    super(data);
    this._uniqueTokenCount = data.UNQTKNCNT;
    this._volatility = data.VLT;
  }

  get uniqueTokenCount(): string {
    return setComma(this._uniqueTokenCount);
  }

  get volatility(): string {
    const sign = this._volatility.startsWith("-") ? "" : "+";
    return sign + setComma(setDigits(this._volatility, 6)) + "%";
  }
}

export class TokenHolderListModel extends ListBaseModel {
  private _totalHolderCount: string;
  private _holderList: Array<EvmTokenHolderInfo>;

  constructor(data: EvmTokenHolderListInfo) {
    super(data.TCNT, data.VCNT);
    this._totalHolderCount = data.THDRCNT;
    this._holderList = data.TKNHDRLIST;
  }

  get totalHolderCount(): string {
    return setComma(this._totalHolderCount);
  }

  get holderList(): Array<TokenHolderModel> {
    return this._holderList.map((holder) => new TokenHolderModel(holder));
  }
}

export class TokenCardModel {
  protected _address: string;
  protected _name: string;
  protected _symbol: string;
  protected _totalSupply: string;
  protected _holders: string;
  protected _holdersChange: string;
  protected _description: string;
  protected _logo: string;
  protected _type: string;

  constructor(data: EvmTokenCardInfo | EvmNftCardInfo) {
    this._address = data.ADDR;
    this._name = data.NM;
    this._symbol = data.SB;
    this._totalSupply = data.TS;
    this._holders = data.HD;
    this._holdersChange = data.HD24H;
    this._description = data.OV;
    this._logo = data.IMG;
    this._type = data.TKNTP;
  }

  get address(): string {
    return this._address;
  }

  get name(): string {
    return this._name;
  }

  get symbol(): string {
    return this._symbol;
  }

  get totalSupply(): string {
    return setComma(setDecimal(this._totalSupply, 2));
  }

  get holders(): string {
    return setComma(this._holders);
  }

  get holdersChange(): string {
    return setComma(setDecimal(this._holdersChange, 2));
  }

  get description(): string {
    return this._description;
  }

  get logo(): string {
    return this._logo;
  }

  get type(): ITokenType {
    return this._type as ITokenType;
  }

  get tokenTypeName() {
    return TOKEN_TYPE_NAME[this.type];
  }
}

export class TokenCardListModel extends ListBaseModel {
  private _tokenCardList: Array<EvmTokenCardInfo>;

  constructor(data: EvmTokenCardListInfo) {
    super(data.TCNT);
    this._tokenCardList = data.CARDLIST;
  }

  get tokenCardList(): Array<TokenCardModel> {
    return this._tokenCardList.map(
      (tokenCard) => new TokenCardModel(tokenCard)
    );
  }
}

/** @description
 * erc20 트랜스퍼 테이블 리스트 모델
 */
export class TokenTransferListModel extends ListBaseModel {
  private _transferList: Array<EvmErc20TransferInfo>;

  constructor(data: EvmErc20TransferListInfo) {
    super(data.TCNT, data.VCNT);
    this._transferList = data.TKNTFLIST;
  }

  get transferList(): Array<TokenTransferModel> {
    return this._transferList.map(
      (transfer) => new TokenTransferModel(transfer)
    );
  }
}

/** @description erc20, erc721, erc1155 공통 상세 정보 모델 */
export class TokenDetailModel {
  private _tokenInfo: EvmTokenSummaryInfo;
  private _totalSupply: string;
  private _totalHolders: string;
  private _totalTransfers: string;
  private _tokenType: string;
  readonly _minPrice: string;
  readonly _maxPrice: string;
  readonly _volume: string;
  private _price: string;
  private _marketCap: string;
  private _fdv: string;

  constructor(data: EvmTokenDetailInfo) {
    this._tokenInfo = data.TKNCTR;
    this._totalSupply = data.TSP;
    this._totalHolders = data.HD;
    this._totalTransfers = data.TTF;
    this._tokenType = data.TKNST;
    this._minPrice = data.MINPRC;
    this._maxPrice = data.MAXPRC;
    this._volume = data.VOL24H;
    this._price = data.PRC ?? "";
    this._marketCap = data.MCAP ?? "";
    this._fdv = data.FDV ?? "";
  }

  get tokenInfo(): TokenSummaryModel {
    return new TokenSummaryModel(this._tokenInfo);
  }

  get tokenType(): ITokenType {
    return this._tokenType as ITokenType;
  }

  get totalSupply(): string {
    return setComma(this._totalSupply);
  }

  get totalSupplyRaw(): string {
    return this._totalSupply;
  }

  get totalSupplyAbbr(): string {
    return setAbbr(this._totalSupply);
  }

  get totalHolders(): string {
    return setComma(this._totalHolders);
  }

  get totalHoldersRaw(): string {
    return this._totalHolders;
  }

  get totalHoldersAbbr(): string {
    return setAbbr(this._totalHolders);
  }

  get totalTransfers(): string {
    return setComma(this._totalTransfers);
  }

  get totalTransfersRaw(): string {
    return this._totalTransfers;
  }

  get totalTransfersAbbr(): string {
    return setAbbr(this._totalTransfers);
  }

  get tokenName(): string {
    return this.tokenInfo.name || this.tokenInfo.address;
  }

  get minPrice(): string {
    return setComma(this._minPrice);
  }

  get maxPrice(): string {
    return setComma(this._maxPrice);
  }

  get volume(): string {
    return setComma(this._volume);
  }

  get price(): string {
    return this._price;
  }

  get marketCap(): string {
    return this._marketCap;
  }

  get fdv(): string {
    return this._fdv;
  }
}

export class NexonNativeTokenInfoModel {
  address: string;
  name: string;
  symbol: string;
  overview: string;
  imgUrl: string;
  price: string;
  marketCap: string;
  fdv: string;
  holders: string;
  totalSupply: string;

  constructor(data: NexonNativeTokenInfo) {
    this.address = data.ADDR;
    this.name = data.NAME;
    this.symbol = data.SB;
    this.overview = data.OVERVIEW;
    this.imgUrl = data.IMGURL;
    this.price = data.PRC;
    this.marketCap = data.MCAP;
    this.fdv = data.FDV;
    this.holders = data.HOLDERS;
    this.totalSupply = data.TS;
  }
}
